import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { RollBlank } from "../models/RollBlank";
import {
	removeRollBlank,
	upsertRollBlank,
} from "../repositories/RollsBlankRepository";
import { ROLLS_BLANK_QUERY_KEYS } from "./useRollsBlanks";

interface MutationOptions {
	onSuccess?: (data?: RollBlank) => void;
	onError?: (error: Error) => void;
}

export const useUpsertRollBlank = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (blank: RollBlank) => {
			return await upsertRollBlank(blank);
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: ROLLS_BLANK_QUERY_KEYS.all,
			});
			onSuccess?.(data);
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		upsertRollBlank: mutation.mutate,
		upsertRollBlankAsync: mutation.mutateAsync,
		isUpserting: mutation.isPending,
	};
};

export const useDeleteRollBlank = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (id: number) => {
			await removeRollBlank(id);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ROLLS_BLANK_QUERY_KEYS.all,
			});
			onSuccess?.();
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		deleteRollBlank: mutation.mutate,
		deleteRollBlankAsync: mutation.mutateAsync,
		isDeleting: mutation.isPending,
	};
};
